import { HSL_ZH_TEMPLATES } from '../../constants/templates/hsl/zh';
import { HSL_EN_TEMPLATES } from '../../constants/templates/hsl/en';

export class HslTextGenerator {
    constructor(language = 'zh') {
        this.setLanguage(language);
    }

    setLanguage(language) {
        this.language = language === 'en' ? 'en' : 'zh';
        this.templates = this.language === 'en' ? HSL_EN_TEMPLATES : HSL_ZH_TEMPLATES;
    }

    formatKeyValue(label, entries) {
        if (!entries || entries.length === 0) return '';
        const text = entries.filter(Boolean).join(this.templates.common.comma);
        return `${this.templates.common.bullet}${label}${this.templates.common.comma}${text}${this.templates.common.period}`;
    }

    mapValue(mapping, value) {
        if (!value) return '';
        return mapping?.[value] || value;
    }

    formatPercent(value) {
        const ratio = Number(value);
        if (!Number.isFinite(ratio)) return '';
        return `${Math.round(ratio * 100)}%`;
    }

    formatDominantHues(hue) {
        if (!hue) return '';
        if (hue.isAchromatic || hue.tone === 'ACHROMATIC') {
            return this.mapValue(this.templates.mappings.hueTone, 'ACHROMATIC');
        }

        const dominantHues = Array.isArray(hue.dominantHues) && hue.dominantHues.length > 0
            ? hue.dominantHues
            : (hue.tone ? [{ tone: hue.tone, ratio: 1 }] : []);
        if (dominantHues.length === 0) return '';

        return dominantHues
            .slice(0, 3)
            .map((item) => {
                const tone = this.mapValue(this.templates.mappings.hueTone, item?.tone);
                const ratio = this.formatPercent(item?.ratio);
                if (!tone) return '';
                if (!ratio) return tone;
                return `${tone}${this.templates.brackets.left}${ratio}${this.templates.brackets.right}`;
            })
            .filter(Boolean)
            .join(this.templates.common.comma);
    }

    formatTemperatureRatios(hue) {
        if (!hue?.temperature || hue?.isAchromatic) return '';

        const ratios = hue?.temperatureRatios;
        if (!ratios) return '';

        const warm = this.formatPercent(ratios.warm);
        const cool = this.formatPercent(ratios.cool);
        const neutral = this.formatPercent(ratios.neutral);
        if (!warm && !cool && !neutral) return '';

        const warmLabel = this.mapValue(this.templates.mappings.hueTemperature, 'WARM');
        const coolLabel = this.mapValue(this.templates.mappings.hueTemperature, 'COOL');
        const neutralLabel = this.mapValue(this.templates.mappings.hueTemperature, 'NEUTRAL');
        return `${warmLabel}/${coolLabel}/${neutralLabel}${this.templates.common.colon}${warm || '0%'} / ${cool || '0%'} / ${neutral || '0%'}`;
    }

    generateLightnessLine(structuredConclusions) {
        const lightness = structuredConclusions?.lightness;
        if (!lightness?.level) return '';
        const parts = [
            this.mapValue(this.templates.mappings.lightnessLevel, lightness.level),
            this.mapValue(this.templates.mappings.dynamicRange, lightness.dynamicRange),
            this.mapValue(this.templates.mappings.skewness, lightness.skewness),
            this.mapValue(this.templates.mappings.kurtosis, lightness.kurtosis)
        ].filter(Boolean);
        return this.formatKeyValue(this.templates.labels.lightness, parts);
    }

    generateHueLine(structuredConclusions) {
        const hue = structuredConclusions?.hue;
        if (!hue) return '';

        const hasHueSignal = Boolean(
            hue.isAchromatic ||
            hue.tone ||
            hue.temperature ||
            hue.distribution ||
            hue.stability ||
            (Array.isArray(hue.dominantHues) && hue.dominantHues.length > 0)
        );
        if (!hasHueSignal) return '';

        const dominantHues = this.formatDominantHues(hue);
        const parts = [
            dominantHues,
            this.mapValue(this.templates.mappings.hueTemperature, hue.temperature),
            this.formatTemperatureRatios(hue),
            this.mapValue(this.templates.mappings.hueDistribution, hue.distribution),
            this.mapValue(this.templates.mappings.hueStability, hue.stability)
        ].filter(Boolean);

        if (parts.length === 0) return '';
        return this.formatKeyValue(this.templates.labels.hue, parts);
    }

    generateSaturationLine(structuredConclusions) {
        const saturation = structuredConclusions?.saturation;
        if (!saturation?.level) return '';
        const parts = [
            this.mapValue(this.templates.mappings.saturationLevel, saturation.level),
            this.mapValue(this.templates.mappings.saturationDistribution, saturation.distribution),
            this.mapValue(this.templates.mappings.saturationStability, saturation.stability),
            this.mapValue(this.templates.mappings.saturationDeviation, saturation.deviation)
        ].filter(Boolean);
        return this.formatKeyValue(this.templates.labels.saturation, parts);
    }

    generateLocalColorLine(structuredConclusions) {
        const localColor = structuredConclusions?.localColor;
        if (!localColor?.balance) return '';
        const balance = this.mapValue(this.templates.mappings.localBalance, localColor.balance);
        return this.formatKeyValue(this.templates.labels.localColor, [balance]);
    }

    generateTextDescriptions(structuredConclusions) {
        const lines = [
            this.generateLightnessLine(structuredConclusions),
            this.generateHueLine(structuredConclusions),
            this.generateSaturationLine(structuredConclusions),
            this.generateLocalColorLine(structuredConclusions)
        ].filter(Boolean);

        if (lines.length === 0) {
            return { overallConclusion: this.templates.noData };
        }

        return { overallConclusion: lines.join('\n') };
    }
}
